import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { Tense } from '@/prakrit/lib/grammar/types'

import { TENSES } from '@/prakrit/lib/grammar/types'
import { FREE_TENSE } from '@/lib/plans'
import { TENSE_LABEL } from '@/prakrit/lib/grammar/labels'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { KalPage } from '@/prakrit/components/grammar/kal/kal-page'
import { PremiumGate } from '@/components/plans/plan-notice'
import { TENSE_INFO, tenseOfSlug, tenseSlug } from '@/prakrit/components/grammar/kal/kal-data'

interface KalTensePageProps {
    tense: Tense
}

const BASIC_TITLE: Partial<Record<Tense, string>> = {
    vartaman: 'Present tense',
    bhoot: 'Past tense',
    bhavishya: 'Future tense',
    vidhi: 'Wish and command'
}

const titleOf = (tense: Tense): string => {
    const en = TENSE_LABEL[tense].en

    return BASIC_TITLE[tense] ?? en.charAt(0).toUpperCase() + en.slice(1)
}

const KalTensePage: FC<KalTensePageProps> = ({ tense }): ReactNode => (
    <Layout>
        <Seo
            title={titleOf(tense)}
            description={`${TENSE_LABEL[tense].hi}: ${TENSE_INFO[tense].use.en} The standard (सामान्य) and variant (विकल्प) Prakrit endings for every person and number, with every form of a root.`}
        />
        {tense === FREE_TENSE
            ? <KalPage tense={tense} />
            : (
                <PremiumGate kind='grammar'>
                    <KalPage tense={tense} />
                </PremiumGate>
            )}
    </Layout>
)

export const getStaticPaths: GetStaticPaths = async () => ({
    paths: TENSES.map(t => ({ params: { tense: tenseSlug(t) } })),
    fallback: false
})

export const getStaticProps: GetStaticProps<KalTensePageProps> = async ({ params }) => {
    const tense = tenseOfSlug(typeof params?.tense === 'string' ? params.tense : '')

    if (!tense) return { notFound: true }

    return { props: { tense } }
}

export default KalTensePage
